// components/AdminLogin.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, AlertCircle } from 'lucide-react';

const AdminLogin = () => {
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Get the redirect path from location state or default to admin dashboard
    const from = location.state?.from?.pathname || '/admin';

    // If already authenticated, redirect to the intended page
    useEffect(() => {
        if (isAuthenticated) {
            navigate(from, { replace: true });
        }
    }, [isAuthenticated, navigate, from]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Simulate slight delay for better UX
        setTimeout(() => {
            const success = login(password);
            if (success) {
                // Redirect to the page they tried to visit or admin dashboard
                navigate(from, { replace: true });
            } else {
                setError('Contraseña incorrecta');
            }
            setLoading(false);
        }, 500);
    };

    return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-800 rounded-2xl p-8 w-full max-w-md"
            >
                <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Lock size={40} className="text-primary" />
                    </div>
                    <h1 className="text-3xl font-bold gradient-text mb-2">
                        Acceso Administrador
                    </h1>
                    <p className="text-gray-400">
                        Ingresa la contraseña para gestionar el contenido
                    </p>
                </div>

                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 bg-red-500/20 border border-red-500 rounded-lg p-4 flex items-center gap-3"
                    >
                        <AlertCircle className="text-red-500" size={20} />
                        <p className="text-red-500 text-sm">{error}</p>
                    </motion.div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="mb-6">
                        <label className="block text-sm font-medium mb-2 text-gray-300">
                            Contraseña
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-primary focus:outline-none"
                            placeholder="********"
                            autoFocus
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-lg font-semibold smooth-transition disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                <span>Verificando...</span>
                            </>
                        ) : (
                            'Ingresar'
                        )}
                    </button>
                </form>

                <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                        Área restringida solo para administradores
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default AdminLogin;